import {
    Controllers,
    CreatedWithMessages,
    IsBadRequestResult,
    IsForbiddenResult,
    IsNotFoundResult,
    IsServerErrorResult,
    ProblemResult
} from "@infoland/iprova-api";
import { HyperlinkPost } from "@/types/todo/hyperlink";
import { AttachmentPost } from "@/types/todo/attachment";
import { Todo } from "@/types/todo/todo";
import { ApiErrorType } from "@/enums";
import Utils from "@/utils";
import storeHelper from "@/store/store-helper";

export interface ActionResult<S, E> {
    success: boolean;
    successData?: S;
    errorData?: E;
}

export interface TodoResult {
    todo?: Todo;
    errorType?: ApiErrorType;
}

export async function getTodoAsync(todoId: number): Promise<TodoResult> {
    const result: Todo = await Controllers.GenericController.getAsync<Todo>(
        `/cases/to_dos/${todoId}?include_resolved_actor_users=true&include_attachments=true&include_hyperlinks=true`
    );
    return handleGetTodoResult(result);
}

export async function updateToDoAsync(todoId: number, patchToDo: object): Promise<ActionResult<void, string>> {
    const result = await Controllers.GenericController.patchAsync<object, void>(`cases/to_dos/${todoId}`, patchToDo);
    return handleResult(result);
}

export async function addHyperlinkAsync(todoId: number, hyperlinkPost: HyperlinkPost): Promise<ActionResult<string, string>> {
    const result = await Controllers.GenericController.postAsync<HyperlinkPost, CreatedWithMessages | ProblemResult<string>>(
        `/cases/to_dos/${todoId}/hyperlinks`,
        hyperlinkPost
    );
    const createdIdentifier = (result as CreatedWithMessages)?.created_identifier;
    if (createdIdentifier) {
        return {
            success: true,
            successData: createdIdentifier
        };
    } else if (IsBadRequestResult(result) || IsForbiddenResult(result) || IsServerErrorResult(result)) {
        storeHelper.siteStore.setSnackBarMessageLookup("general.messages.error", true);
        return {
            success: false
        };
    }
}
export async function deleteHyperlinkAsync(hyperlinkId: number): Promise<ActionResult<void, string>> {
    const result = await Controllers.GenericController.deleteAsync<null, void>(`/cases/hyperlinks/${hyperlinkId}`);
    return handleResult(result);
}

export async function addAttachmentAsync(todoId: number, attachmentPost: AttachmentPost): Promise<ActionResult<string, string>> {
    const result = await Controllers.GenericController.postAsync<AttachmentPost, CreatedWithMessages | ProblemResult<string>>(
        `/cases/to_dos/${todoId}/attachments`,
        attachmentPost
    );
    const createdIdentifier = (result as CreatedWithMessages)?.created_identifier;
    if (createdIdentifier) {
        return {
            success: true,
            successData: createdIdentifier
        };
    } else if (IsBadRequestResult(result) || IsForbiddenResult(result) || IsServerErrorResult(result)) {
        storeHelper.siteStore.setSnackBarMessageLookup("general.messages.error", true);
        return {
            success: false
        };
    }
}
export async function deleteAttachmentAsync(attachmentId: number): Promise<ActionResult<void, string>> {
    const result = await Controllers.GenericController.deleteAsync<null, void>(`cases/attachments/${attachmentId}`);
    return handleResult(result);
}

function handleGetTodoResult(result: Todo | ProblemResult<string>): TodoResult {
    const response = { todo: null, errorType: null } as TodoResult;

    if (IsServerErrorResult(result)) {
        const response = { todo: null, errorType: null, showError: false };
        response.errorType = ApiErrorType.InternalServerError;
        return response;
    }
    if (IsForbiddenResult(result)) {
        response.errorType = ApiErrorType.Forbidden;
        return response;
    }
    if (IsNotFoundResult(result)) {
        response.errorType = ApiErrorType.NotFound;
        return response;
    }
    if (IsBadRequestResult(result)) {
        response.errorType = ApiErrorType.BadRequest;
        return response;
    }

    response.todo = result;
    return response;
}

function handleResult(result: void | ProblemResult<string>): ActionResult<void, string> {
    if (IsServerErrorResult(result)) {
        storeHelper.siteStore.setSnackBarServerErrorMessage(result);
        return { success: false };
    } else if (IsBadRequestResult(result) || IsForbiddenResult(result)) {
        storeHelper.siteStore.setSnackBarMessageLookup("general.messages.error", true);
        return { success: false };
    } else {
        return { success: true };
    }
}
